import axios from "axios";
import { EMAIL_PROVIDERS } from "@/common/const";

/**
	* @typedef {Object} OAuth2Dto
	* @property {string} inboxEmail - Email address (used to identify token on backend)
	*/

/**
	* @typedef {Object} OutlookListQueryDto
	* @property {number} [limit]
	* @property {string} [pageToken]
	* @property {string} [query]
	* @property {"desc" | "asc"} [orderBy]
	* @property {"Inbox" | "SentItems" | "DeletedItems" | "JunkEmail" | "flagged"} [folder]
	*/

/**
	* @typedef {Object} EmailListQueryDto
	* @property {number} [limit]
	* @property {string} [pageToken]
	* @property {string} [query]
	* @property {string[]} [labelIds]
	* @property {boolean} [includeSpamTrash]
	* @property {"desc" | "asc"} [orderBy]
	*/

/**
	* @typedef {Object} EmailThreadListResponseView
	* @property {EmailThreadListItemView[]} threads - Список тредов (разговоров), каждый с сообщениями и фрагментом.
	* @property {string} [nextPageToken] - Токен для загрузки следующей страницы (если есть).
	* @property {number} [resultSizeEstimate] - Оценка общего количества результатов.
	*/

/**
	* @typedef {Object} EmailThreadListItemView
	* @property {string} threadId - Уникальный идентификатор треда.
	* @property {string} snippet - Краткий фрагмент (превью) последнего сообщения в треде.
	* @property {EmailListItemView[]} messages - Сообщения, входящие в тред.
	*/

/**
	* @typedef {Object} EmailListItemView
	* @property {string} id - Уникальный идентификатор сообщения.
	* @property {string} threadId - Идентификатор треда, к которому относится сообщение.
	* @property {string} subject - Тема письма.
	* @property {string} from - Адрес отправителя.
	* @property {string} [to] - Адрес получателя (если доступен).
	* @property {string} date - Дата отправки или получения сообщения (ISO-строка).
	* @property {string} [snippet] - Краткий фрагмент текста сообщения.
	* @property {boolean} unread - Признак непрочитанности.
	* @property {boolean} starred - Признак "важного" письма (звёздочка).
	* @property {boolean} [hasAttachments] - Есть ли вложения в письме.
	* @property {"gmail" | "outlook"} provider - Источник письма.
	*/

const emailInboxService = {
	/**
		* Delete multiple email threads from Trash
		* @param {string[]} threadIds - Array of Gmail thread IDs
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<{ successIds: string[], failedIds: string[] }>}
		*/
	async deleteThreadsFromTrash(threadIds, auth, type) {
		const { data } = await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/threads/trash-delete`, {
			inboxEmail: auth.inboxEmail,
			threadIds:[...threadIds]
		});

		return data;
	},

	/**
		* Download email attachment
		* @param {string} emailId
		* @param {string} attachmentId
		* @param {OAuth2Dto} auth
		* @returns {Promise<Blob>}
		*/
	async downloadAttachment(emailId, attachmentId, auth, type) {
		const { data } = await axios.post(
			`/email-sync/${EMAIL_PROVIDERS[type]}/emails/${emailId}/attachments/${attachmentId}`,
			auth,
			{ responseType: "blob" }
		);

		return data;
	},

	/**
		* Get email by ID
		* @param {string} emailId
		* @param {OAuth2Dto} auth
		* @returns {Promise<EmailView>}
		*/
	async getEmailById(emailId, auth, type) {
		const { data } = await axios.post(`/email-sync/${EMAIL_PROVIDERS[type]}/emails/${emailId}`, auth);

		return data;
	},

	/**
		* Get full messages in a email thread
		* @param {string} threadId
		* @param {OAuth2Dto} auth
		* @returns {Promise<EmailView[]>}
		*/
	async getFullThreadById(threadId, auth, type) {
		const { data } = await axios.post(`/email-sync/${EMAIL_PROVIDERS[type]}/threads/${threadId}`, auth);

		return data;
	},

	/**
		* List email threads
		* @param {EmailListQueryDto|OutlookListQueryDto} query
		* EmailListQueryDto - For Gmail
		* OutlookListQueryDto - For Outlook
		* @param {OAuth2Dto} auth
		* @returns {Promise<EmailThreadListResponseView>}
		*/
	async listThreads(query, auth, type) {
		const { data } = await axios.post(`/email-sync/${EMAIL_PROVIDERS[type]}/threads`, auth, {
			params: query
		});

		return data;
	},

	/**
		* Mark email as read
		* @param {string} emailId
		* @param {OAuth2Dto} auth
		* @returns {Promise<void>}
		*/
	async markEmailAsRead(emailId, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/${emailId}/read`, auth);
	},

	/**
		* Move email email to Trash
		* @param {string} emailId - The ID of the Gmail message
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<void>}
		*/
	async moveEmailToTrash(emailId, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/${emailId}/trash`, auth);
	},

	/**
		* Move multiple email threads to Trash
		* @param {string[]} threadIds - Array of Gmail thread IDs
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<{ successIds: string[], failedIds: string[] }>}
		*/
	async moveThreadsToTrash(threadIds, auth, type) {
		const { data } = await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/threads/trash`, {
			inboxEmail: auth.inboxEmail,
			threadIds:[...threadIds]
		});

		return data;
	},

	/**
		* Restore multiple email threads from Trash back to Inbox
		* @param {string[]} threadIds - Array of Gmail thread IDs
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<{ successIds: string[], failedIds: string[] }>}
		*/
	async restoreThreadsFromTrash(threadIds, auth, type) {
		const { data } = await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/threads/untrash`, {
			inboxEmail: auth.inboxEmail,
			threadIds: [...threadIds]
		});

		return data;
	},

	/**
		* Send email without linking to a patient
		* @param {FormData} formData - Should include fields from SendEmailDto and optional attachments
		* @returns {Promise<any>}
		*/
	async sendEmailManual(formData, type) {
		const { data } = await axios.post(`/email-sync/${EMAIL_PROVIDERS[type]}/send`, formData);

		return data;
	},

	/**
		* Set email email as read
		* @param {string} emailId - Gmail message ID
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<void>}
		*/
	async setEmailAsRead(emailId, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/${emailId}/toggle-read`, { oAuth2Dto: auth, read: true });
	},

	/**
		* Set email email as unread
		* @param {string} emailId - Gmail message ID
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<void>}
		*/
	async setEmailAsUnread(emailId, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/${emailId}/toggle-read`, { oAuth2Dto: auth, read: false });
	},

	/**
		* Toggle starred status for a Gmail email
		* @param {string} emailId - The ID of the Gmail message
		* @param {boolean} starred - Whether to mark (true) or unmark (false) the message as starred
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<void>}
		*/
	async toggleStarredStatus(emailId, starred, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/${emailId}/star`, {
			oAuth2Dto: auth,
			starred
		});
	},

	/**
		* Toggle spam status of a thread (move to or remove from spam folder)
		* @param {string} threadId - Thread ID (conversationId)
		* @param {boolean} isSpam - true = mark as spam, false = remove from spam
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @param {"gmail" | "outlook"} type - Email provider
		* @returns {Promise<void>}
		*/
	async toggleThreadSpamStatus(threadId, isSpam, auth, type) {
		await axios.put(`/email-sync/${EMAIL_PROVIDERS[type]}/threads/${threadId}/toggle-spam`, {
			oAuth2Dto: auth,
			isSpam
		});
	},

	/**
		* Toggle flagged status for a Outlook email
		* @param {string} emailId - The ID of the Gmail message
		* @param {boolean} flagged - Whether to mark (true) or unmark (false) the message as flagged
		* @param {OAuth2Dto} auth - Object containing inboxEmail
		* @returns {Promise<void>}
		*/
	async toggleFlaggedStatus(emailId, flagged, auth) {
		await axios.put(`/email-sync/outlook/${emailId}/flag`, {
			oAuth2Dto: auth,
			flagged: flagged
		});
	}
};

export default emailInboxService;
